/**
 * MatchEntityRegistry — Single source of truth for match entity instances.
 * Application layer only (no Three.js / DOM).
 *
 * Contrato:
 *   add / remove / getById / setPlayer / getPlayer
 *   getChampions / getStructures / getDamageable / size
 */

const STRUCTURE_TYPES = new Set(['turret', 'inhibitor', 'nexus']);

export class MatchEntityRegistry {
  constructor() {
    this._byId = new Map();
    this._playerId = null;
  }

  get size() {
    return this._byId.size;
  }

  add(entity) {
    if (!entity || !entity.id) {
      throw new Error('MatchEntityRegistry.add requires entity with id');
    }
    if (this._byId.has(entity.id)) {
      throw new Error(`MatchEntityRegistry duplicate id: ${entity.id}`);
    }
    this._byId.set(entity.id, entity);
    return entity;
  }

  remove(id) {
    this._byId.delete(id);
    if (this._playerId === id) {
      this._playerId = null;
    }
  }

  getById(id) {
    return this._byId.get(id) ?? null;
  }

  setPlayer(entity) {
    if (!entity || !this._byId.has(entity.id)) {
      throw new Error('MatchEntityRegistry.setPlayer requires a registered entity');
    }
    this._playerId = entity.id;
  }

  getPlayer() {
    if (!this._playerId) return null;
    return this.getById(this._playerId);
  }

  getChampions() {
    return this._all().filter((e) => e.type === 'champion');
  }

  getStructures() {
    return this._all().filter((e) => STRUCTURE_TYPES.has(e.type));
  }

  getDamageable({ includeDead = false } = {}) {
    return this._all().filter((e) => includeDead || !e.isDead);
  }

  getAll() {
    return this._all();
  }

  clear() {
    this._byId.clear();
    this._playerId = null;
  }

  _all() {
    return [...this._byId.values()];
  }
}
